/**
 * Terra Ingestion Zod Schemas - Single Source of Truth
 *
 * This file contains all Zod schemas for terra-ingestion payloads.
 * All schemas are verified against production database payloads.
 *
 * Key production findings:
 * - referenceKey is at plan.referenceKey, NOT inside flatPlan
 * - actionContext keys are sender-owned: callbackUrl (webhook senders) and
 *   recordId + objectApiName (salesforcePatch senders) are the ones Terra reads
 * - InteractivePlan has reason and context fields for AI output
 *
 * @see plans/robust-test-fixtures-generator.md for verification details
 */
import { z } from 'zod';
import { JsonValueSchema } from './json-value';

// Ingestion Contract Constants

/**
 * The artifact field used as the ingestion idempotency key.
 * - Ingestion deduplicates on this (prisma upsert by externalId column)
 * - Webhook payloads send it as referenceKey by default
 * - The API input field is the snake_case form: external_id
 *
 * Both the Prisma column (externalId) and API field (external_id) are
 * fixed by schema, but this constant names the *concept* so the webhook
 * handler and ingestion pipeline are explicitly linked.
 */
export const INGESTION_IDEMPOTENCY_FIELD = 'externalId';

// Field & Option Definitions

/** Canonical option list for picklists/multipicklists */
export const OptionListZ = z.array(z.string());

/** Lookup source for reference fields (verified in production flat plans) */
export const LookupSourceZ = z.object({
  parentObject: z.string(),
  lookupField: z.string(),
  contextId: z.string().optional(),
});

/**
 * Field Definition - verified against production flat plans
 *
 * Production examples:
 * - Flat: { apiName: "Email Summary", label: "Email Summary", dataType: "textarea", maxLength: 5000 }
 */
export const FieldDefinitionZ = z.object({
  apiName: z.string(),
  label: z.string(),
  dataType: z.string(),
  required: z.boolean().optional(),
  maxLength: z.number().optional(),
  options: OptionListZ.optional(),
  lookupSource: LookupSourceZ.optional(),
  searchMinChars: z.number().optional(),
  referenceTo: z.string().optional(),
  // Production shows picklist values in some payloads
  picklistValues: z.array(z.object({
    value: z.string(),
    label: z.string().optional(),
    active: z.boolean().optional(),
  })).optional(),
});

// Plan Items

/** Flat plan item (single-step field suggestion) */
export const FlatPlanItemZ = z.object({
  field: FieldDefinitionZ,
  proposedValue: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.null(),
  ]).optional(),
  // Middleman API can return confidence as number or string (e.g., "0.85")
  confidence: z.union([z.number(), z.string()]).optional(),
});

/**
 * Flat Plan - production-verified structure
 *
 * IMPORTANT: referenceKey is NOT in this schema!
 * Production shows referenceKey at plan.referenceKey level, not flatPlan.referenceKey
 */
export const FlatPlanZ = z.object({
  planType: z.literal('flat'),
  actionType: z.string(),
  // Sender-owned context bag: the three typed keys are the ones Terra reads
  // (webhook senders set callbackUrl, salesforcePatch senders set recordId +
  // objectApiName); anything else the sender includes must survive the parse,
  // so this object is passthrough, not a closed whitelist.
  actionContext: z.object({
    callbackUrl: z.string().optional(),
    recordId: z.string().optional(),
    objectApiName: z.string().optional(),
  }).passthrough().optional(),
  items: z.array(FlatPlanItemZ),
});

// Plan Wrapper

/**
 * Plan wrapper - contains mode, referenceKey, and the actual plan payload
 *
 * Production example:
 * {
 *   "mode": "single_step",
 *   "referenceKey": "00Ta700000dXiyM",  // <-- AT THIS LEVEL!
 *   "flatPlan": { "planType": "flat", "actionType": "sendObject", ... }
 * }
 */
export const PlanZ = z.object({
  mode: z.enum(['single_step', 'wizard']),
  // referenceKey lives HERE, not inside flatPlan (verified against production)
  referenceKey: z.string().optional(),
  // For single_step mode
  flatPlan: FlatPlanZ.optional(),
  // For wizard mode - stored as untyped (salesforce_wizard artifact type is retired)
  initial: z.unknown().optional(),
  interactivePlan: z.unknown().optional(),
});

// Approval Metadata

/**
 * Approval metadata for Terra/Clay writeback
 * All required fields must be non-empty strings
 */
export const ApprovalMetadataZ = z.object({
  viewId: z.string().min(1),
  tableId: z.string().min(1),
  uniqueId: z.union([
    z.record(z.string(), z.string()),
    z.string().min(1),
  ]),
  updateColumn: z.string().min(1),
  userFeedbackColumn: z.string().min(1),
  recordId: z.string().optional(),
  updateFieldId: z.string().optional(),
});

// Envelope V2

/** Action configuration in envelope */
export const ActionZ = z.object({
  kind: z.string(),
  salesforce: z.object({
    objectApiName: z.string().optional(),
    recordId: z.string().optional(),
  }).optional(),
});

/** Context object which may contain approval metadata */
export const ContextZ = z.object({
  title: z.string().optional(),
  summary: z.string().optional(),
  approvalMetadata: ApprovalMetadataZ.optional(),
});

/**
 * Envelope V2 Schema - the canonical V2 envelope structure
 *
 * This is the normalized envelope format that all payloads are translated to.
 * Legacy V1 payloads are translated to this format by the adapter.
 */
export const EnvelopeV2Z = z.object({
  schemaVersion: z.literal(2),
  type: z.enum(['approval', 'input', 'insight']),
  category: z.string().nullable().optional(),
  artifactTypeLabel: z.string().nullable().optional(),
  artifactTypeSlug: z.string().max(64).regex(/^[a-z0-9_]*$/).nullable().optional(),
  plan: PlanZ.optional(),
  action: ActionZ.optional(),
  context: ContextZ.optional(),
  links: z.unknown().optional(),
  renderHints: z.object({
    markdownFields: z.array(z.string()).optional(),
    markdown: z.boolean().optional(),
  }).optional(),
  raw: z.unknown().optional(),
});

// V1 Legacy Payload Schema

/**
 * Accepts any string a sender might mean as a timestamp, matching the
 * pre-schema behavior of `new Date(timestamp)` in TerraIngestionService with
 * no format check. Excludes only strings `new Date` itself cannot resolve
 * (e.g. "not-a-date"); a bare-digit string is treated as an epoch value
 * since `new Date("<digits>")` (a Date.parse call, not a Number coercion)
 * returns NaN for those even though the pre-schema service accepted them.
 */
export const TerraTimestampZ = z.string().refine(
  s => /^-?\d+$/.test(s) || !Number.isNaN(new Date(s).getTime()),
  { message: 'must be a parseable date string or an epoch numeric string' },
);

/**
 * V1 (Legacy) Terra Ingestion Payload
 *
 * This is the shape of payloads sent by upstream Clay tables.
 * These are translated to V2 envelope format by the adapter.
 */
export const V1PayloadZ = z.object({
  // Required fields
  // Cap is generous headroom over any real external_id shape (SF ids are 18
  // chars; composite dedup keys run longer) while keeping the derived DBOS
  // workflow id / btree index entries well under the postgres size limit.
  external_id: z.string().min(1).max(512),
  owner_slack: z.string().min(1),
  type: z.string(),
  category: z.string(),

  // Common optional fields
  object_id: z.string().optional(),
  content: z.unknown().optional().default({}),
  metadata: z.unknown().optional(),
  timestamp: TerraTimestampZ.optional(),
  artifact_name: z.string().optional(),
  ownerEmail: z.string().optional(),
  owner_email: z.string().optional(), // deprecated alias
  status: z.string().optional(),
  summary: z.string().optional(),
  links: z.unknown().optional(),
  secondary_object_id: z.string().optional(),
  canvas_url: z.string().optional(),
  salesforce_record_url: z.string().optional(),
  execution_user_answers: z.unknown().optional(),
  execution_result_message: z.string().optional(),
  execution_error_code: z.string().optional(),
  account_name: z.string().optional(),
  markdown_fields: z.array(z.string()).optional(),
});

// V2 Payload Schema (with envelope)

/**
 * scripts/convert-webhook-v1-to-v2.ts (a real, still-used dev tool) emits V2
 * payloads with envelope.schemaVersion === 2 but no top-level schemaVersion —
 * intentional per this endpoint's own apidoc (top-level schemaVersion is
 * backward-compat only, never required). Fill it in from envelope.schemaVersion
 * before the object shape is checked, so the accepted shape is declared once.
 */
function withSchemaVersionFromEnvelope(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const obj = value as Record<string, unknown>;
  const envelope = obj.envelope;
  const envelopeSchemaVersion = envelope && typeof envelope === 'object' && !Array.isArray(envelope)
    ? (envelope as Record<string, unknown>).schemaVersion
    : undefined;
  if (envelopeSchemaVersion === 2 && obj.schemaVersion !== 2) {
    return { ...obj, schemaVersion: 2 };
  }
  return value;
}

/**
 * V2 Terra Ingestion Payload - includes envelope field
 *
 * V2 payloads have schemaVersion: 2 at the top level and include
 * an envelope field with the V2 envelope structure.
 *
 * Note: As of 2026-01-05, no production clients send V2 payloads yet.
 * All source_schema_version values are 1 or null.
 */
export const V2PayloadZ = z.preprocess(withSchemaVersionFromEnvelope, z.object({
  // Required V2 identifiers
  schemaVersion: z.literal(2),
  // See V1PayloadZ above for why 512.
  external_id: z.string().min(1).max(512),
  owner_slack: z.string().min(1),

  // The V2 envelope
  envelope: EnvelopeV2Z,

  // Common optional fields (same as V1)
  object_id: z.string().optional(),
  content: z.unknown().optional().default({}),
  metadata: z.unknown().optional(),
  timestamp: TerraTimestampZ.optional(),
  artifact_name: z.string().optional(),
  ownerEmail: z.string().optional(),
  owner_email: z.string().optional(),
  summary: z.string().optional(),
  links: z.unknown().optional(),
  secondary_object_id: z.string().optional(),
  canvas_url: z.string().optional(),
  salesforce_record_url: z.string().optional(),
  execution_user_answers: z.unknown().optional(),
  execution_result_message: z.string().optional(),
  execution_error_code: z.string().optional(),
  account_name: z.string().optional(),
  markdown_fields: z.array(z.string()).optional(),
}));

// Union Type for Incoming Payloads

/**
 * Terra Ingestion Payload - accepts either V1 or V2 format
 *
 * Use this for validating incoming webhook payloads.
 */
export const TerraIngestionPayloadZ = z.union([V2PayloadZ, V1PayloadZ]);

// Stored Raw V1 Payload Schema

/**
 * Schema for raw_v1_payload column - stored subset of V1 payload data.
 *
 * IMPORTANT: This differs from V1PayloadZ which is for incoming webhooks.
 * The raw_v1_payload column stores a subset of the original V1 payload,
 * excluding external_id and owner_slack (stored as separate columns).
 *
 * @see src/services/terra-ingestion-service.ts rawV1Payload construction
 */
export const StoredRawV1PayloadZ = z.object({
  // Core classification fields
  category: z.string().optional(),
  type: z.string().optional(),
  artifact_name: z.string().optional(),
  summary: z.string().optional(),
  object_id: z.string().optional(),
  account_id: z.string().optional(),
  account_name: z.string().optional(),

  // Complex nested data
  metadata: z.unknown().optional(),
  content: z.unknown().optional().default({}),

  // Other V1 fields
  status: z.string().optional(),
  links: z.unknown().optional(),
  markdown_fields: z.array(z.string()).optional(),
  canvas_url: z.string().optional(),
  salesforce_record_url: z.string().optional(),
}).passthrough(); // Intentional: reads stored DB data that may contain fields not in this schema

// V4 Ingestion Schema

const CORRELATION_ID_RE = /^[a-zA-Z0-9_\-.]{1,255}$/;

/**
 * V4 Instance Ingestion Payload — single source of truth
 *
 * Used at the route boundary (src/api/v4/ingestion.ts) for Zod validation
 * and in the service layer (src/services/v4-ingestion.ts) via z.infer<>.
 */
export const V4IngestionRequestSchema = z.object({
  kind: z.string().min(1, 'kind is required'),
  // See V1PayloadZ above for why 512.
  external_id: z.string().min(1, 'external_id is required').max(512),
  owner_slack_id: z.string().min(1, 'owner_slack_id is required'),
  salesforce_object_id: z.string().optional(),
  target_salesforce_object_id: z.string().optional(),
  correlation_id: z.string()
    .refine(v => v !== '.' && v !== '..', 'correlation_id cannot be "." or ".." (path traversal prevention)')
    .refine(v => CORRELATION_ID_RE.test(v), 'correlation_id must match ^[a-zA-Z0-9_\\-.]{1,255}$')
    .optional(),
  data: z.record(z.string(), JsonValueSchema).refine(v => v !== null && !Array.isArray(v), 'data must be a non-null object'),
  summary: z.string().optional(),
});

// Type Exports

export type OptionList = z.infer<typeof OptionListZ>;
export type LookupSource = z.infer<typeof LookupSourceZ>;
export type FieldDefinition = z.infer<typeof FieldDefinitionZ>;
export type FlatPlanItem = z.infer<typeof FlatPlanItemZ>;
export type FlatPlan = z.infer<typeof FlatPlanZ>;
export type Plan = z.infer<typeof PlanZ>;
export type ApprovalMetadata = z.infer<typeof ApprovalMetadataZ>;
export type Action = z.infer<typeof ActionZ>;
export type Context = z.infer<typeof ContextZ>;
export type EnvelopeV2 = z.infer<typeof EnvelopeV2Z>;
export type V1Payload = z.infer<typeof V1PayloadZ>;
export type V2Payload = z.infer<typeof V2PayloadZ>;
export type TerraIngestionPayload = z.infer<typeof TerraIngestionPayloadZ>;
export type StoredRawV1Payload = z.infer<typeof StoredRawV1PayloadZ>;
export type V4InstancePayload = z.infer<typeof V4IngestionRequestSchema>;

// Parse Helpers

export function parseEnvelopeV2(value: unknown): EnvelopeV2 {
  return EnvelopeV2Z.parse(value);
}

export function safeParseTerraPayload(value: unknown): ReturnType<typeof TerraIngestionPayloadZ.safeParse> {
  return TerraIngestionPayloadZ.safeParse(value);
}

export function parseFlatPlan(value: unknown): FlatPlan {
  return FlatPlanZ.parse(value);
}

/**
 * Loose envelope validation schema - matches the old validateEnvelopeV2() behavior.
 * Only validates the top-level envelope structure, not deeply nested plan contents.
 * This is needed because the adapter builds envelopes from legacy data that may not
 * conform to the strict plan schemas.
 */
export const EnvelopeV2LooseZ = z.object({
  schemaVersion: z.literal(2),
  type: z.enum(['approval', 'input', 'insight']),
  category: z.unknown().optional(),
  artifactTypeLabel: z.unknown().optional(),
  artifactTypeSlug: z.unknown().optional(),
  // Intentional .passthrough(): loose validation for legacy adapter output; strict variant is EnvelopeV2Z
  plan: z.object({
    mode: z.enum(['single_step', 'wizard']),
  }).passthrough().optional(),
  action: z.object({
    kind: z.string(),
  }).passthrough().optional(),
  context: z.unknown().optional(),
  links: z.unknown().optional(),
  renderHints: z.unknown().optional(),
  raw: z.unknown().optional(),
}).passthrough().refine( // Intentional .passthrough(): loose schema; use EnvelopeV2Z for strict validation
  (data) => {
    // input type requires plan with mode
    if (data.type === 'input') {
      return data.plan && typeof data.plan === 'object' && 'mode' in data.plan;
    }
    return true;
  },
  { message: 'input type requires plan with mode' }
);

/**
 * Validate an envelope and return structured errors
 * Replaces the manual validateEnvelopeV2() function
 *
 * Uses loose validation (EnvelopeV2LooseZ) to match the old validator behavior.
 * For strict validation of plan contents, use EnvelopeV2Z directly.
 */
export function validateEnvelope(value: unknown): { valid: true } | { valid: false; errors: string[] } {
  const result = EnvelopeV2LooseZ.safeParse(value);
  if (result.success) {
    return { valid: true };
  }
  const errors = result.error.issues.map(e =>
    `${e.path.join('.')}: ${e.message}`
  );
  return { valid: false, errors };
}

/**
 * Strict envelope validation that checks all nested plan contents.
 * Use this for validating fixtures before posting.
 */
export function validateEnvelopeStrict(value: unknown): { valid: true } | { valid: false; errors: string[] } {
  const result = EnvelopeV2Z.safeParse(value);
  if (result.success) {
    return { valid: true };
  }
  const errors = result.error.issues.map(e =>
    `${e.path.join('.')}: ${e.message}`
  );
  return { valid: false, errors };
}

/**
 * Schema for the terra_ingestion.envelope DB column.
 *
 * Derived from EnvelopeV2LooseZ (passthrough + refine logic inherited).
 * Narrows the three string-typed fields (category, artifactTypeLabel,
 * artifactTypeSlug) and the record-typed fields (context, renderHints)
 * from z.unknown() to concrete types so the inferred type satisfies
 * the EnvelopeV2 interface used downstream. Prod data sampled 2026-06-12
 * (122,657 rows) confirms these fields are string-or-absent, never non-string.
 *
 * Use with parseDbJson:
 *
 *   parseDbJson(TerraIngestionEnvelopeSchema, raw.envelope, {
 *     column: 'terra_ingestion.envelope',
 *     rowId: raw.id,
 *   })
 */
export const TerraIngestionEnvelopeSchema = EnvelopeV2LooseZ.safeExtend({
  category: z.string().nullable().optional(),
  artifactTypeLabel: z.string().nullable().optional(),
  artifactTypeSlug: z.string().nullable().optional(),
  context: z.record(z.string(), z.unknown()).optional(),
  renderHints: z.record(z.string(), z.unknown()).optional(),
});
